import { getDataSource } from '@/lib/data-source';
import { DataSourceAdapter, RequestConfig } from '@/lib/data-source/types';

/**
 * Unified API client that uses data source abstraction
 * Automatically switches between mock and live data based on configuration
 */
export default class ApiClient {
  private dataSource: DataSourceAdapter;

  constructor() {
    this.dataSource = getDataSource();
  }

  /**
   * Get current data source (useful for debugging)
   */
  getDataSource(): DataSourceAdapter {
    return this.dataSource;
  }

  /**
   * Check if currently using mock data
   */
  isUsingMockData(): boolean {
    return this.dataSource.source === 'mock';
  }

  /**
   * GET request
   */
  async get<T = Record<string, unknown>>(url: string, config?: RequestConfig) {
    const response = await this.dataSource.get<T>(url, config);
    return { 
      data: response.data, 
      status: response.status, 
      message: response.message,
      meta: response.meta,
    };
  }

  /**
   * POST request
   */
  async post<T = Record<string, unknown>, D = unknown>(url: string, data?: D, config?: RequestConfig) {
    const response = await this.dataSource.post<T>(url, data, config);
    return { 
      data: response.data, 
      status: response.status, 
      message: response.message,
      meta: response.meta,
    };
  }

  /**
   * PUT request
   */
  async put<T = Record<string, unknown>, D = unknown>(url: string, data?: D, config?: RequestConfig) {
    const response = await this.dataSource.put<T>(url, data, config);
    return { 
      data: response.data, 
      status: response.status, 
      message: response.message,
      meta: response.meta,
    };
  }

  /**
   * PATCH request
   */
  async patch<T = Record<string, unknown>, D = unknown>(url: string, data?: D, config?: RequestConfig) {
    const response = await this.dataSource.patch<T>(url, data, config);
    return { 
      data: response.data, 
      status: response.status, 
      message: response.message,
      meta: response.meta,
    };
  }

  /**
   * DELETE request
   */
  async delete<T = Record<string, unknown>>(url: string, config?: RequestConfig) {
    const response = await this.dataSource.delete<T>(url, config);
    return { 
      data: response.data, 
      status: response.status, 
      message: response.message,
      meta: response.meta,
    };
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
